import { useEffect, useState } from 'react';
import { Capacitor } from '@capacitor/core';
import NativeApp from './app';
import NativeCamera from './camera';
import NativeFilesystem from './filesystem';
import NativeKeyboard from './keyboard';
import NativeNetwork from './network';
import NativePush from './push';
import NativeShare from './share';
import NativeCashfree from './cashfree';
import NativeStatusBar from './statusBar';
import NativeSplash from './splash';

export {
  NativeApp,
  NativeCamera,
  NativeFilesystem,
  NativeKeyboard,
  NativeNetwork,
  NativePush,
  NativeShare,
  NativeCashfree,
  NativeStatusBar,
  NativeSplash
};

export const isNativePlatform = () => Capacitor.isNativePlatform();
export const getPlatform = () => Capacitor.getPlatform();

/**
 * Custom React hook for auto-bootstrapping Native mobile features
 * Handles splash screen auto-hide, status bar styling, hardware back-button, and network listeners.
 */
export const useNativeBootstrap = (options = {}) => {
  const [isOnline, setIsOnline] = useState(true);
  const [networkType, setNetworkType] = useState('wifi');
  const isNative = isNativePlatform();

  useEffect(() => {
    // 1. Configure Status Bar & Hide Splash Screen
    const initDisplay = async () => {
      if (isNative) {
        await NativeStatusBar.setStyle('DARK');
        await NativeStatusBar.setBackgroundColor('#0f172a');
        await NativeSplash.hide(300);
      }
    };
    initDisplay();

    // 2. Network Listener
    let networkListenerSub = null;
    NativeNetwork.getStatus().then((status) => {
      setIsOnline(status.connected);
      setNetworkType(status.connectionType);
    });

    const sub = NativeNetwork.onStatusChange((status) => {
      setIsOnline(status.connected);
      setNetworkType(status.connectionType);
      if (!status.connected) {
        console.warn('[NEXDESK Mobile] Device is offline');
      }
    });

    if (sub && typeof sub.then === 'function') {
      sub.then((res) => { networkListenerSub = res; });
    } else {
      networkListenerSub = sub;
    }

    // 3. Android Back Button Handler
    let backButtonSub = null;
    if (isNative) {
      const backSub = NativeApp.onBackButton(({ canGoBack }) => {
        if (window.history.length > 1) {
          window.history.back();
        } else {
          // At root history, ask to exit
          if (window.confirm('Do you want to exit NEXDESK?')) {
            NativeApp.exitApp();
          }
        }
      });

      if (backSub && typeof backSub.then === 'function') {
        backSub.then((res) => { backButtonSub = res; });
      } else {
        backButtonSub = backSub;
      }

      // 4. Initialize Push Notifications on Mobile
      NativePush.init().catch((e) => console.warn('[Push] Auto-init failed:', e.message));
    }

    return () => {
      if (networkListenerSub && typeof networkListenerSub.remove === 'function') {
        networkListenerSub.remove();
      }
      if (backButtonSub && typeof backButtonSub.remove === 'function') {
        backButtonSub.remove();
      }
    };
  }, [isNative]);

  return {
    isNative,
    isOnline,
    networkType,
    platform: getPlatform()
  };
};

export default {
  App: NativeApp,
  Camera: NativeCamera,
  Filesystem: NativeFilesystem,
  Keyboard: NativeKeyboard,
  Network: NativeNetwork,
  Push: NativePush,
  Share: NativeShare,
  Cashfree: NativeCashfree,
  StatusBar: NativeStatusBar,
  Splash: NativeSplash,
  isNative: isNativePlatform,
  getPlatform
};
